import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import HomePage from '@/pages/Home';
import AboutPage from '@/pages/About';
import TeachersPage from '@/pages/Teachers';
import CareersPage from '@/pages/Careers';
import ContactPage from '@/pages/Contact';
import ProductsPage from '@/pages/Products';
import SchoolERPPage from '@/pages/SchoolERP';
import CollegeERPPage from '@/pages/CollegeERP';
import LynktPage from '@/pages/Lynkt';
import LMSPage from '@/pages/LMS';
import ComingSoonPage from '@/pages/ComingSoon';
import PrivacyPage from '@/pages/Privacy';
import TermsPage from '@/pages/Terms';
import CookiesPage from '@/pages/Cookies';
import '@/index.css';

import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const lenis = new Lenis({
      autoRaf: false,
      duration: 1.4,
      easing: (t) => 1 - Math.pow(1 - t, 4),
      smoothWheel: true,
      syncTouch: false,
      touchMultiplier: 2,
      wheelMultiplier: 1,
    });

    lenisRef.current = lenis;

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    ScrollTrigger.scrollerProxy(document.documentElement, {
      scrollTop(value) {
        if (arguments.length) {
          lenis.scrollTo(value, { immediate: true });
        }
        return lenis.scroll;
      },
      getBoundingClientRect() {
        return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
      },
      pinType: document.documentElement.style.transform ? 'transform' : 'fixed',
    });

    ScrollTrigger.defaults({ scroller: document.documentElement });

    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const target = document.querySelector(hash);
        if (target) lenis.scrollTo(target, { offset: -100 });
      }
    };
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      lenis.destroy();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/teachers" element={<TeachersPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          {/* Product routes */}
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/school-erp" element={<SchoolERPPage />} />
          <Route path="/products/college-erp" element={<CollegeERPPage />} />
          <Route path="/products/lynkt" element={<LynktPage />} />
          <Route path="/products/lms" element={<LMSPage />} />
          {/* Legal pages */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          {/* Coming soon pages */}
          <Route path="/press" element={<ComingSoonPage />} />
          <Route path="/docs" element={<ComingSoonPage />} />
          <Route path="/blog" element={<ComingSoonPage />} />
          <Route path="/support" element={<ComingSoonPage />} />
          {/* Fallback */}
          <Route path="*" element={<HomePage />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
